import { takeEvery, put, call, CallEffect, PutEffect } from "redux-saga/effects";

import {
  GET_PROFILE,
  AGREE_TO_PROFILE_TERMS,
  SAVE_PROFILE_BILLING,
  SAVE_PROFILE_SHIPPING,
  SAVE_PROFILE_CONTACT,
  SAVE_PROFILE_CREDENTIALS,
  SAVE_PROFILE_EXPERIENCE,
  SAVE_PROFILE_AREA,
  SAVE_PROFILE_SHIPPING_AREA,
  CREATE_GLBA,
  UPDATE_GLBA_GENERAL,
  UPDATE_GLBA_SECURITY,
  UPDATE_GLBA_ACCESS,
  SAVE_OUT_OF_OFFICE
} from "./actionTypes";

import {
  getProfileOk,
  getProfileErr,
  saveProfileOk,
  saveProfileErr,
  createGlbaOk,
  createGlbaErr,
  updateGlbaGeneralOk,
  updateGlbaGeneralErr,
  updateGlbaSecurityOk,
  updateGlbaSecurityErr,
  updateGlbaAccessOk,
  updateGlbaAccessErr,
  saveOutOfOfficeOk,
  saveOutOfOfficeErr,
} from "./actions";

import {
  getMyProfile,
  agreeToProfileTerms,
  saveMyProfileBilling,
  saveMyProfileShipping,
  saveMyProfileContact,
  saveMyProfileCredentials,
  saveMyProfileExperience,
  saveMyProfileArea,
  saveMyProfileShippingArea,
  createNotaryGlba,
  updateGeneral,
  updateSecurity,
  updateAccess,
  saveOutOfOffice
} from "~/helpers/backendHelper";

import {
  GetMyProfileResponse,
  SaveProfileResponse,
  CreateNotaryGlbaResponse,
  UpdateGeneralResponse,
  UpdateSecurityResponse,
  UpdateAccessResponse,
  SaveOutOfOfficeResponse,
} from "~/types/backend";
import { TAction } from "~/types/global";

/********** GET PROFILE **********/

function* onGetProfile(): Generator<CallEffect | PutEffect<any>, void, GetMyProfileResponse> {
  try {
    const response = yield call(getMyProfile);
    yield put(getProfileOk(response));
  } catch (error) {
    yield put(getProfileErr(error as Error));
  }
}

/********** SAVE PROFILE **********/

function* onAgreeToProfileTerms(action: TAction): Generator<CallEffect | PutEffect<any>, void, SaveProfileResponse> {
  try {
    const response = yield call(agreeToProfileTerms, action.payload.data);
    yield put(saveProfileOk(response));
  } catch (error) {
    yield put(saveProfileErr(error as Error));
  }
}

function* onSaveProfileBilling(action: TAction): Generator<CallEffect | PutEffect<any>, void, SaveProfileResponse> {
  try {
    const response = yield call(saveMyProfileBilling, action.payload.data);
    yield put(saveProfileOk(response));
  } catch (error) {
    yield put(saveProfileErr(error as Error));
  }
}

function* onSaveProfileShipping(action: TAction): Generator<CallEffect | PutEffect<any>, void, SaveProfileResponse> {
  try {
    const response = yield call(saveMyProfileShipping, action.payload.data);
    yield put(saveProfileOk(response));
  } catch (error) {
    yield put(saveProfileErr(error as Error));
  }
}

function* onSaveProfileContact(action: TAction): Generator<CallEffect | PutEffect<any>, void, SaveProfileResponse> {
  try {
    const response = yield call(saveMyProfileContact, action.payload.data);
    yield put(saveProfileOk(response));
  } catch (error) {
    yield put(saveProfileErr(error as Error));
  }
}

function* onSaveProfileCredentials(action: TAction): Generator<CallEffect | PutEffect<any>, void, SaveProfileResponse> {
  try {
    const response = yield call(saveMyProfileCredentials, action.payload.data);
    yield put(saveProfileOk(response));
  } catch (error) {
    yield put(saveProfileErr(error as Error));
  }
}

function* onSaveProfileExperience(action: TAction): Generator<CallEffect | PutEffect<any>, void, SaveProfileResponse> {
  try {
    const response = yield call(saveMyProfileExperience, action.payload.data);
    yield put(saveProfileOk(response));
  } catch (error) {
    yield put(saveProfileErr(error as Error));
  }
}

function* onSaveProfileArea(action: TAction): Generator<CallEffect | PutEffect<any>, void, SaveProfileResponse> {
  try {
    const response = yield call(saveMyProfileArea, action.payload.data);
    yield put(saveProfileOk(response));
  } catch (error) {
    yield put(saveProfileErr(error as Error));
  }
}

function* onSaveProfileShippingArea(action: TAction): Generator<CallEffect | PutEffect<any>, void, SaveProfileResponse> {
  try {
    const response = yield call(saveMyProfileShippingArea, action.payload.data);
    yield put(saveProfileOk(response));
  } catch (error) {
    yield put(saveProfileErr(error as Error));
  }
}

function* onCreateGlba(action: TAction): Generator<CallEffect | PutEffect<any>, void, CreateNotaryGlbaResponse> {
  try {
    const response = yield call(createNotaryGlba, action.payload.id, action.payload.data);
    yield put(createGlbaOk(response));
  } catch (error) {
    yield put(createGlbaErr(error as Error));
  }
}

function* onUpdateGlbaGeneral(action: TAction): Generator<CallEffect | PutEffect<any>, void, UpdateGeneralResponse> {
  try {
    const response = yield call(updateGeneral, action.payload.id, action.payload.data);
    yield put(updateGlbaGeneralOk(response));
  } catch (error) {
    yield put(updateGlbaGeneralErr(error as Error));
  }
}

function* onUpdateGlbaSecurity(action: TAction): Generator<CallEffect | PutEffect<any>, void, UpdateSecurityResponse> {
  try {
    const response = yield call(updateSecurity, action.payload.id, action.payload.data);
    yield put(updateGlbaSecurityOk(response));
  } catch (error) {
    yield put(updateGlbaSecurityErr(error as Error));
  }
}

function* onUpdateGlbaAccess(action: TAction): Generator<CallEffect | PutEffect<any>, void, UpdateAccessResponse> {
  try {
    const response = yield call(updateAccess, action.payload.id, action.payload.data);
    yield put(updateGlbaAccessOk(response));
  } catch (error) {
    yield put(updateGlbaAccessErr(error as Error));
  }
}

function* onSaveProfileOutOfOffice(action: TAction): Generator<CallEffect | PutEffect<any>, void, SaveOutOfOfficeResponse> {
  try {
    const response = yield call(saveOutOfOffice, action.payload.id, action.payload.data);
    yield put(saveOutOfOfficeOk(response));
  } catch (error) {
    yield put(saveOutOfOfficeErr(error as Error));
  }
}

function* profileSaga() {
  yield takeEvery(GET_PROFILE, onGetProfile);
  yield takeEvery(AGREE_TO_PROFILE_TERMS, onAgreeToProfileTerms);
  yield takeEvery(SAVE_PROFILE_BILLING, onSaveProfileBilling);
  yield takeEvery(SAVE_PROFILE_SHIPPING, onSaveProfileShipping);
  yield takeEvery(SAVE_PROFILE_CONTACT, onSaveProfileContact);
  yield takeEvery(SAVE_PROFILE_CREDENTIALS, onSaveProfileCredentials);
  yield takeEvery(SAVE_PROFILE_EXPERIENCE, onSaveProfileExperience);
  yield takeEvery(SAVE_PROFILE_AREA, onSaveProfileArea);
  yield takeEvery(SAVE_PROFILE_SHIPPING_AREA, onSaveProfileShippingArea);
  yield takeEvery(CREATE_GLBA, onCreateGlba);
  yield takeEvery(UPDATE_GLBA_GENERAL, onUpdateGlbaGeneral);
  yield takeEvery(UPDATE_GLBA_SECURITY, onUpdateGlbaSecurity);
  yield takeEvery(UPDATE_GLBA_ACCESS, onUpdateGlbaAccess);
  yield takeEvery(SAVE_OUT_OF_OFFICE, onSaveProfileOutOfOffice);
}

export default profileSaga;
